import { Formik, ErrorMessage, Field, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { faAddressCard, faTimesCircle } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import _, { debounce } from 'lodash';
import classNames from 'classnames';
import moment from 'moment';
import { AdultParticipant, ChildParticipant, ParentGuardian, ParticipantBase, Registration } from '../../../api/program.registration.models';
import { DateInputField } from '../../../common/components/DateInputField';
import { PhoneInputField } from '../../../common/components/PhoneInputField';
import { getCountries, getProvincesStates, getProvinceLabel, getPostalCodeLabel } from '../../../common/address';
import { useEffect, useMemo, useState } from 'react';
import { useUserAddressQuery, useUserPeopleQuery } from '../../../api/user.api';
import { useAuthState } from '../../../state/auth.state';
import { useRegistrationState } from '../functions/registration-state';
import { Address, Person } from '../../../api/user.models';
import { useCartState } from '../../cart/functions/cart-state';

const ParentGuardianSchema = Yup.object().shape({
	ParentGuardian: Yup.object().shape({
		FirstName: Yup.string()
			.min(2, 'Too Short!')
			.max(50, 'Too Long!')
			.required('Required'),
		LastName: Yup.string()
			.min(2, 'Too Short!')
			.max(50, 'Too Long!')
			.required('Required'),
		Email: Yup.string().email('Invalid email').required('Required'),
		HomePhone: Yup.string().min(10, 'Not a valid phone number').when('MobilePhone', { is: undefined, then: (schema) => schema.required('Required') }),
		MobilePhone: Yup.string().min(10, 'Not a valid phone number').when('HomePhone', { is: undefined, then: (schema) => schema.required('Required') }),
		SameAddress: Yup.boolean(),
		Address: Yup.object().shape({
			Street1: Yup.string().required('Required'),
			Street2: Yup.string(),
			City: Yup.string().required('Required'),
			Country: Yup.string().required('Required'),
			Province: Yup.string(),
			PostalCode: Yup.string().required('Required'),
		}),
		Location: Yup.string().required('Required'),
		EmergencyContact: Yup.string().required('Required')
	}, [['HomePhone', 'MobilePhone']])
});

interface ProgramRegistrationAdultProps {
	participant: ParentGuardian;
	index: number;
	onRemove: (index: number) => void;
}

const ProgramRegistrationParentGuardian = ({ participant, index, onRemove }: ProgramRegistrationAdultProps) => {
	const { registration } = useRegistrationState();

	const isAdult = true;
	const isChild = false;
	const salutations = ["", "Mr", "Mrs", "Ms", "Dr", "Rev"];
	
	const handleCountryChanged = (country: string, values: { ParentGuardian: ParticipantBase }) => {
		if (country === "Canada") {
			values.ParentGuardian.Address!.Province = "Ontario";
			values.ParentGuardian.Address!.Region = "";
		}
		else if (country === "United States") {
			values.ParentGuardian.Address!.Province = "Alabama";
			values.ParentGuardian.Address!.Region = "";
		}
		else
			values.ParentGuardian.Address!.Province = "";
	}

	const showRemove = registration.ParentGuardians.length > 1;

	const showUseSameAddress = index > 0;
	
	return <>
		<p className="lead font-weight-bold mb-2">
			<FontAwesomeIcon icon={faAddressCard} className="mr-2" />
			Please provide information for Parent/Guardian #{index+1}
		</p>

		<Formik
			validationSchema={ParentGuardianSchema}
			enableReinitialize={true}
			initialValues={{
				ParentGuardian: _.cloneDeep(participant),
				GuestType: "adult"
			}}
			onSubmit={(values, { setSubmitting }) => {

			}}
			validateOnBlur={true}
		>
		{({ isSubmitting, submitCount, isValid, values, errors, handleChange }) => <>

			<AutoSaveAdultState ItemIndex={0} GuestIndex={index} GuestType={"adult"} />
			<div className="form-row">
				<div className="col-12 col-sm-6">
					<AutofillPerson />
				</div>
				{showRemove &&
				<div className="col-12 col-sm-6 text-right">
					<button type="button" onClick={() => onRemove(index)} className="btn btn-link mt-1 p-0"><FontAwesomeIcon icon={faTimesCircle} className="mr-2" />Remove this Parent/Guardian</button>
				</div>
				}
			</div>
			<div className="form-row">
				{isAdult && 
				<div className={classNames(["col-6 col-sm-2", isChild ? 'pr-sm-0-x':''])}>
					<div className="form-label-group">
						<Field component="select" name="ParentGuardian.Salutation" className={classNames(["custom-select", values.ParentGuardian.Salutation !== '' ? "value-selected":""])}>
							{salutations.map((salutation, i) => {
								return <option key={i}>{salutation}</option>
							})}
						</Field>
						<label className="text-truncate">Salutation</label>
					</div>
				</div>
				}
				<div className={classNames(["col-12", isAdult ? "col-sm-4 pl-sm-0-x":"", isChild ? "col-sm-3 pr-sm-0-x":""])}>
					<div className="form-label-group">
						<Field type="text" name={`ParentGuardian.FirstName`} className={classNames([`form-control`, errors.ParentGuardian?.FirstName ? 'is-invalid':''])} placeholder={true ? "Required" : ""} autoComplete="off" />
						<label htmlFor={`firstName_${index}`}>First Name</label>
						<ErrorMessage name="ParentGuardian.FirstName" component="div" className="error-message fw-bold small text-danger" />
						{/* {!isUnique(values) &&
						<div className="error-message fw-bold small text-danger">Name is duplicated</div>
						} */}
					</div>
				</div>
				<div className={classNames(["col-12 col-sm-4", isAdult ? "pl-sm-0-x":"", isChild ? "pr-sm-0-x":""])}>
					<div className="form-label-group">
						<Field type="text" name={`ParentGuardian.LastName`} className={classNames([`form-control`, errors.ParentGuardian?.LastName ? 'is-invalid':''])} placeholder={true ? "Required" : ""} autoComplete="off" />
						<label>Last Name</label>
						<ErrorMessage name="ParentGuardian.LastName" component="div" className="error-message fw-bold small text-danger" />
						{/* {!isUnique(values) &&
						<div className="error-message fw-bold small text-danger">Name is duplicated</div>
						} */}
					</div>
				</div>
				<div className={classNames(["col-6 col-sm-2", isAdult ? "pl-sm-0-x":"", isChild ? 'pr-sm-0-x':''])}>
					<div className="form-label-group">
						<Field component="select" name="ParentGuardian.Gender" className={classNames(["custom-select", values.ParentGuardian.Gender !== 0 ? "value-selected":""])}>
							<option value={0}></option>
							<option value={1}>Male</option>
							<option value={2}>Female</option>
						</Field>
						<label className="text-truncate">Gender</label>
					</div>
				</div>
				{isChild &&
					<div className={classNames(["col-12 col-sm-3", "pr-sm-0-x"])}>
						<div className="form-label-group">
							<DateInputField name={`ParentGuardian.Birthdate`} minDate={moment().add(-100, "years").toDate()} maxDate={moment().toDate()} className={`form-control`} placeholder={true ? "Required" : ""} autoComplete="off" />
							<label>Date of Birth</label>
							<ErrorMessage name="ParentGuardian.Birthdate" component="div" className="error-message fw-bold small text-danger" />
						</div>
					</div>
				}

			</div>

			{isAdult && 
			<div className="form-row">
				<div className="col-12 col-sm-4">
					<div className="form-label-group">
						<Field type="text" name={`ParentGuardian.Email`} className={classNames([`form-control`, errors.ParentGuardian?.Email ? 'is-invalid':''])} placeholder={true ? "Email (required)" : ""} autoComplete="off" required />
						<label>Email</label>
						<ErrorMessage name="ParentGuardian.Email" component="div" className="error-message fw-bold small text-danger" />
					</div>
				</div>
				<div className="col-12 col-sm-4">
					<div className="form-label-group">
						<PhoneInputField name="ParentGuardian.HomePhone" className={classNames([`form-control`, errors.ParentGuardian?.HomePhone ? 'is-invalid':''])} placeholder="Required" autoComplete="off" required={values.ParentGuardian.MobilePhone === "" || values.ParentGuardian.MobilePhone == null} />
						<label>Home Phone</label>
						<ErrorMessage name="ParentGuardian.HomePhone" component="div" className="error-message fw-bold small text-danger" />
					</div>
				</div>
				<div className="col-12 col-sm-4">
					<div className="form-label-group">
						<PhoneInputField name="ParentGuardian.MobilePhone" className={classNames([`form-control`, errors.ParentGuardian?.MobilePhone ? 'is-invalid':''])} placeholder={values.ParentGuardian.MobilePhone === "" || values.ParentGuardian.MobilePhone == null ? "required":"required"} autoComplete="off" required={values.ParentGuardian.HomePhone === "" || values.ParentGuardian.HomePhone == null}  />
						<label>Cell Phone</label>
						<ErrorMessage name="ParentGuardian.MobilePhone" component="div" className="error-message fw-bold small text-danger" />
					</div>
				</div>
			</div>
			}


			{showUseSameAddress && <>
			<div className="form-row">
				<div className="col-12">
					<div className="custom-control custom-checkbox mb-2">					
						<Field type="checkbox" name="ParentGuardian.SameAddress" id={`ParentGuardian.SameAddress${index}`} checked={values.ParentGuardian.SameAddress} className="custom-control-input" />
						<label htmlFor={`ParentGuardian.SameAddress${index}`} className="custom-control-label">Use same address as Parent/Guardian #1</label>
					</div>		
				</div>
			</div>

			{values.ParentGuardian.SameAddress &&
			<div className="form-row">
				<div className="col-12 mb-2">
					<span>Address: </span>
					{values.ParentGuardian.Address?.Street1}{values.ParentGuardian.Address?.Street1 ? ", " : ""}
					{values.ParentGuardian.Address?.Street2}{values.ParentGuardian.Address?.Street2 ? ", " : ""}
					{values.ParentGuardian.Address?.City}{values.ParentGuardian.Address?.City ? ", " : ""}
					{values.ParentGuardian.Address?.Province}{values.ParentGuardian.Address?.Province ? ", " : ""}
					{values.ParentGuardian.Address?.PostalCode}{values.ParentGuardian.Address?.PostalCode ? ", " : ""}
					{values.ParentGuardian.Address?.Country}
				</div>
			</div>
			}
			</>}
			
			{(showUseSameAddress === false || values.ParentGuardian.SameAddress === false) && 
			<>
			<div className="form-row">
				<div className="col-12 col-sm-6">
					<AutofillAddress />
				</div>
			</div>

			<div className="form-row">
				<div className="col-12 col-sm-4">
					<div className="form-label-group">
						<Field type="text" name={`ParentGuardian.Address.Street1`} className={classNames([`form-control`, _.get(errors.ParentGuardian, "Address.Street1") ? 'is-invalid':''])} placeholder={true ? "required" : ""} autoComplete="off" required={false} />
						<label>Street Address</label>
						<ErrorMessage name="ParentGuardian.Address.Street1" component="div" className="fw-bold small text-danger" />
					</div>
				</div>
				<div className="col-12 col-sm-4">
					<div className="form-label-group">
						<Field type="text" name={`ParentGuardian.Address.Street2`} className={`form-control`} placeholder={false ? "required" : " "} autoComplete="off" required={false} />
						<label>Apt / Unit Number</label>
						<ErrorMessage name="ParentGuardian.Address.Street2" component="div" className="fw-bold small text-danger" />
					</div>
				</div>
				<div className="col-12 col-sm-4">
					<div className="form-label-group">
						<Field type="text" name={`ParentGuardian.Address.City`} className={classNames([`form-control`, _.get(errors.ParentGuardian, "Address.City") ? 'is-invalid':''])} placeholder={false ? "required" : " "} autoComplete="off" required={false} />
						<label>City</label>
						<ErrorMessage name="ParentGuardian.Address.City" component="div" className="error-message fw-bold small text-danger" />
					</div>
				</div>
				<div className={classNames(["col-6 col-sm-4"])}>
					<div className="form-label-group">
						<Field component="select" name="ParentGuardian.Address.Country" onChange={(e:any) => { handleCountryChanged(e.target.value, values); handleChange(e); }} className={classNames(["custom-select", values.ParentGuardian.Address?.Country !== "" ? "value-selected":""])}>
							{getCountries().map((country, index) => {
								return <option key={index} value={country}>{country}</option>
							})}
						</Field>
						<label className="text-truncate">Country</label>
					</div>
				</div>
				{(values.ParentGuardian.Address?.Country === "Canada" || values.ParentGuardian.Address?.Country === "United States") &&
				<div className={classNames(["col-6 col-sm-4"])}>
					<div className="form-label-group">
						<Field component="select" name="ParentGuardian.Address.Province" className={classNames(["custom-select", values.ParentGuardian.Address?.Province !== "" ? "value-selected":""])}>
							{getProvincesStates(values.ParentGuardian.Address?.Country ?? "").map((province, index) => {
								return <option key={index} value={province}>{province}</option>
							})}
						</Field>
						<label className="text-truncate">{getProvinceLabel(values.ParentGuardian.Address?.Country ?? "")}</label>
					</div>
				</div>
				}
				{(values.ParentGuardian.Address?.Country !== "Canada" && values.ParentGuardian.Address?.Country !== "United States") &&
				<div className="col-12 col-sm-4">
					<div className="form-label-group">
						<Field type="text" name={`ParentGuardian.Address.Region`} className={`form-control`} placeholder={" "} autoComplete="off" required={false} />
						<label>Region</label>
						<ErrorMessage name="ParentGuardian.Address.Region" component="div" className="error-message fw-bold small text-danger" />
					</div>
				</div>
				}
				<div className="col-12 col-sm-4">
					<div className="form-label-group">
						<Field type="text" name={`ParentGuardian.Address.PostalCode`} className={classNames([`form-control`, _.get(errors.ParentGuardian, "Address.PostalCode") ? 'is-invalid':''])} placeholder={false ? "required" : " "} autoComplete="off" required={false} />
						<label className="text-truncate">{getPostalCodeLabel(values.ParentGuardian.Address?.Country ?? "")}</label>
						<ErrorMessage name="ParentGuardian.Address.PostalCode" component="div" className="error-message fw-bold small text-danger" />
					</div>
				</div>
			</div>

			</>}

			<div className="form-row">
				<div className="col-12">
					<p className="form-text font-weight-bold mb-2">Emergency Contact Information</p>
				</div>	
				<div className="col-12 col-sm-6">
					<div className="form-label-group mb-sm-0">
						<Field component="textarea" rows="3" maxLength="100" name={`ParentGuardian.Location`} className={classNames([`form-control`, errors.ParentGuardian?.Location ? 'is-invalid':''])} placeholder={false ? "required" : " "} autoComplete="off" required={false} />
						<label>Room # / Site # / Address</label>
						<span className="text-muted small">({100-(values.ParentGuardian.Location != null ? values.ParentGuardian.Location.length : 0)}/100 characters remaining)</span>
					</div>
					<ErrorMessage name="ParentGuardian.Location" component="div" className="fw-bold small text-danger" />
				</div>
				<div className="col-12 col-sm-6">
					<div className="form-label-group mb-0">
						<Field component="textarea" rows="3" maxLength="100" name={`ParentGuardian.EmergencyContact`} className={classNames([`form-control`, errors.ParentGuardian?.EmergencyContact ? 'is-invalid':''])} placeholder={false ? "required" : " "} autoComplete="off" required={false} />
						<label>Emergency Contact #</label>
						<span className="text-muted small">({100-(values.ParentGuardian.EmergencyContact != null ? values.ParentGuardian.EmergencyContact.length : 0)}/100 characters remaining)</span>
					</div>
					<ErrorMessage name="ParentGuardian.EmergencyContact" component="div" className="fw-bold small text-danger" />
				</div>
			</div>

		</>}

		</Formik>


		<hr />
	</>
}

export default ProgramRegistrationParentGuardian


interface AutoSaveAdultStateProps {
	ItemIndex: number,
	GuestIndex: number,
	GuestType: "adult"|"child"
}

const AutoSaveAdultState = ({ ItemIndex, GuestIndex, GuestType }: AutoSaveAdultStateProps) => {
	const { registration, updateRegistration, emitRegistrationParticipants, useRegistrationValidationListener, useRegistrationParticipantsListener } = useRegistrationState();
	const { values, submitForm, isValidating } = useFormikContext<{ ParentGuardian: AdultParticipant|ChildParticipant }>();

	useRegistrationValidationListener(() => {
		submitForm();
	});

	const update = useMemo(() => debounce((registration: Registration, values: { ParentGuardian: AdultParticipant|ChildParticipant }) => {
		if (values.ParentGuardian) {		
			if (GuestType === "adult") { 
				registration.ParentGuardians[GuestIndex] = values.ParentGuardian as AdultParticipant;
			}
			// if (GuestType === "child") {
			// 	registration.Adults[GuestIndex] = values.ParentGuardian as ChildParticipant;
			// }
			updateRegistration(registration);
			emitRegistrationParticipants(registration);
				
		}
	}, 1000), []);

	useEffect(() => {
		update(registration, values);
	}, [isValidating])

	const updateSameAddress = useMemo(() => debounce((res: Registration ,values: { ParentGuardian: ParentGuardian|AdultParticipant }) => {
		if (values.ParentGuardian.SameAddress) {
			values.ParentGuardian.Address = res.ParentGuardians[0].Address;
		}
		updateRegistration(res);
	}, 1000), []);

	useRegistrationParticipantsListener((reg: Registration) => {
		updateSameAddress(reg, values);
	});

	useEffect(() => {
		if (GuestIndex > 0 || GuestType === "adult") {
			updateSameAddress(registration, values);
		}
	}, [values.ParentGuardian.SameAddress, isValidating])
	
	return <></>
}


interface AutofillPersonProps {
	// ItemIndex: number
	// GuestIndex: number
	// GuestType: "adult"|"child"
}

const AutofillPerson = ({ }: AutofillPersonProps) => {
	const { values, setValues, validateForm } = useFormikContext<{ ParentGuardian: AdultParticipant|ChildParticipant, GuestType: string }>();
	// const { validateReservation } = useRegistrationState();
	const { isAuthenticated } = useAuthState();
	const { getCartAutofillParentGuardians, getCartAutofillAdultParticipants } = useCartState();
	const { data: userPeople } = useUserPeopleQuery();

	const getAge = (person: Person) : number|null => {
		if (person.Birthdate == null)
			return null;

		return moment().diff(moment(person.Birthdate), "years");
	}
	
	// const autofillAdults = userPeople?.map(p => p.Person)?.filter(p => { return p != null && (p.Birthdate == null || (getAge(p) != null && getAge(p)! > 18 )) }) ?? [];
	// const autofillChildren = userPeople?.map(p => p.Person)?.filter(p => { return p != null && (p.Birthdate != null && (getAge(p) != null && getAge(p)! <= 18 )) }) ?? [];

	const [autofillPerson, setAutofillPerson] = useState("");

	let autofillPeople = userPeople?.map(p => p.Person).filter(p => ((values.GuestType === "adult" && p.Birthdate === null) || (values.GuestType === "child" && p.Birthdate != null && getAge(p) != null && getAge(p)! <= 18))).map(p => p) ?? [];
	let autofillParentGuardians = getCartAutofillParentGuardians();
	autofillParentGuardians = autofillParentGuardians.concat(getCartAutofillAdultParticipants());

	const handleAutofillPerson = async (value: string) => {
		let autofillPerson = autofillPeople.find(a => a.Id === parseInt(value));
		if (autofillPerson === undefined)
			return handleAutofillParentGuardian(value);

		if (values.GuestType === "adult") {
			let guest = values.ParentGuardian as AdultParticipant;
			guest.Salutation = autofillPerson.Salutation;
			guest.FirstName = autofillPerson.FirstName;
			guest.LastName = autofillPerson.LastName;
			guest.Gender = autofillPerson.Gender;
			guest.Email = autofillPerson.PersonalEmail;
			guest.HomePhone = autofillPerson.HomePhone;
			guest.MobilePhone = autofillPerson.MobilePhone;

			setValues(values);
		}

		setAutofillPerson("");

		await validateForm();
		await validateForm();
	}

	const handleAutofillParentGuardian = async (value: string) => {
		let autofillParentGuardian = autofillParentGuardians.find(a => a.Id === value);
		if (autofillParentGuardian === undefined)
			return;

		if (values.GuestType === "adult") {
			let guest = values.ParentGuardian as AdultParticipant;
			guest.Salutation = autofillParentGuardian.Salutation;
			guest.FirstName = autofillParentGuardian.FirstName;
			guest.LastName = autofillParentGuardian.LastName;
			guest.Gender = autofillParentGuardian.Gender;
			guest.Email = autofillParentGuardian.Email;
			guest.HomePhone = autofillParentGuardian.HomePhone;
			guest.MobilePhone = autofillParentGuardian.MobilePhone;
			guest.EmergencyContact = autofillParentGuardian.EmergencyContact;
			guest.Location = autofillParentGuardian.Location;

			setValues(values);
		}

		setAutofillPerson("");

		await validateForm();
		await validateForm();
	}

	return !isAuthenticated() ? <></> : <>
		<select value={autofillPerson} onChange={(e) => handleAutofillPerson(e.target.value)} className="custom-select custom-select-sm w-100 mb-3 text-info font-weight-bold">
			<option>Autofill with person...</option>
			{autofillPeople?.map((person, i) => {
				return values.GuestType === "adult" ? 
					<option key={i} value={person.Id}>{person.FirstName} {person.LastName}, {person.PersonalEmail}, {person.HomePhone}</option> : 
					<option key={i} value={person.Id}>{person.FirstName} {person.LastName}</option>
			})}
			{autofillParentGuardians.map((parentGuardian, i) => {
				return <option key={i} value={parentGuardian.Id}>{parentGuardian.FirstName} {parentGuardian.LastName}, {parentGuardian.Email}, {parentGuardian.HomePhone}</option>
			})}
		</select>
	</>
}

interface AutofillAddressProps {
	// ItemIndex: number
	// GuestIndex: number
	// GuestType: "adult"|"child"
}

const AutofillAddress = ({ }: AutofillAddressProps) => {
	const { values, setValues, validateForm } = useFormikContext<{ ParentGuardian: AdultParticipant|ChildParticipant }>();
	// const { validateReservation } = useRegistrationState();
	const { isAuthenticated } = useAuthState();
	const { getCartAutofillAddresses } = useCartState();
	const { data: userAddresses } = useUserAddressQuery();

	const [autofillAddress, setAutofillAddress] = useState("");
	let autofillAddresses = userAddresses?.map(a => a.Address) ?? [];
	let autofillCartAddresses = getCartAutofillAddresses();

	const handleAutofillAddress = async (value: string) => {
		let autofillAddress = autofillAddresses.find(a => a.Street1 === value);
		if (autofillAddress === undefined) {
			autofillAddress = autofillCartAddresses.find(a => a.Street1 === value) as Address|undefined;
		}
		if (autofillAddress === undefined)
			return;

		values.ParentGuardian.Address = autofillAddress;
		setValues(values);

		setAutofillAddress("");

		await validateForm();
		await validateForm();

		// await validateReservation();
	}

	return !isAuthenticated() ? <></> : <>
		<select value={autofillAddress} onChange={(e) => handleAutofillAddress(e.target.value)} className="custom-select custom-select-sm w-100 mb-3 text-info font-weight-bold">
			<option>Autofill with address...</option>
			{autofillAddresses?.map((address, i) => {
				return <option key={i} value={address.Street1}>{address.Street1}, {address.City}, {address.Province}, {address.PostalCode}, {address.Country}</option>
			})}
			{autofillCartAddresses?.map((address, i) => {
				return <option key={i} value={address.Street1}>{address.Street1}, {address.City}, {address.Province}, {address.PostalCode}, {address.Country}</option>
			})}
		</select>
	</>
}